import { useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import { useDenyWarEnabled } from "../../../hooks/useDenyWarEnabled";
import { GET_TOP_5_TIPPERS_BY_AMOUNT } from "../../../queries/broadcasterQueries";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "../../charts/RechartsWrapper";
import Loading from "../../Loading";

interface TipAmountOption {
  name: string;
  amount: number;
  changeDate?: string;
  newAmount?: number;
}

// Hardcoded tip amounts that broadcasters offer
const TIP_AMOUNTS: TipAmountOption[] = [
  { name: "Deny", amount: 666 },
  { name: "Undeny", amount: 555, changeDate: "03-07-2025", newAmount: 669 },
  { name: "Instacum", amount: 1111 },
  { name: "Headpats", amount: 55 },
  { name: "Spank Deck", amount: 140 },
  { name: "Lover Boy", amount: 1222 },
  { name: "Spanks", amount: 65 },
  { name: "Hydrate", amount: 7 },
  { name: "Confused Boner", amount: 8 },
  { name: "Butt Plug", amount: 275 },
  { name: "Gag", amount: 299 },
  { name: "Shots", amount: 499 },
  { name: "Dinner", amount: 500, changeDate: "03-07-2025", newAmount: 555 },
];

const DENY_WAR_RANGE = "deny war";

export const TopTippersPage = () => {
  const [selectedTip, setSelectedTip] = useState<TipAmountOption>(
    TIP_AMOUNTS[0]
  );
  const [range, setRange] = useState("month");

  const { loading, error, data } = useQuery(GET_TOP_5_TIPPERS_BY_AMOUNT, {
    variables: {
      tipAmount: selectedTip,
      range,
    },
  });
  const { isDenyWarEnabled } = useDenyWarEnabled();

  // Fall back to the default range if wars get turned off while it's selected.
  useEffect(() => {
    if (!isDenyWarEnabled && range === DENY_WAR_RANGE) {
      setRange("month");
    }
  }, [isDenyWarEnabled, range]);

  if (loading) return <Loading />;
  if (error) return <div className="text-red-500">Error: {error.message}</div>;

  const topTippers = data?.getTop5TippersByAmount;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">
            Top Tippers by Tip
          </h1>
          <p className="text-gray-400">
            View the top 5 supporters for each tip amount
          </p>
        </div>

        {/* Controls */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8 border border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Tip Amount Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-3">
                Select Tip Amount
              </label>
              <select
                value={selectedTip.name}
                onChange={(e) => {
                  const tip = TIP_AMOUNTS.find(
                    (t) => t.name === e.target.value
                  );
                  if (tip) setSelectedTip(tip);
                }}
                className="w-full bg-gray-700 text-white border border-gray-600 rounded px-4 py-2 hover:bg-gray-600 transition"
              >
                {TIP_AMOUNTS.map((tip) => (
                  <option key={tip.name} value={tip.name}>
                    {tip.name} ({tip.amount} tokens)
                  </option>
                ))}
              </select>
            </div>

            {/* Time Range Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-3">
                Time Range
              </label>
              <select
                value={range}
                onChange={(e) => setRange(e.target.value)}
                className="w-full bg-gray-700 text-white border border-gray-600 rounded px-4 py-2 hover:bg-gray-600 transition"
              >
                <option value="month">This Month</option>
                <option value="last-month">Last Month</option>
                <option value="year">This Year</option>
                <option value="last-year">Last Year</option>
                {isDenyWarEnabled ? (
                  <option value={DENY_WAR_RANGE}>Latest Deny War</option>
                ) : null}
                <option value="allTime">All Time</option>
              </select>
            </div>
          </div>
        </div>

        {/* Bar Chart */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
            <h2 className="text-2xl font-bold text-white">
              Top 5: {topTippers?.name}
            </h2>
            <p className="text-purple-100 text-sm mt-1">
              For the selected {range}
            </p>
          </div>

          {topTippers?.topTippers && topTippers.topTippers.length > 0 ? (
            <div className="p-6">
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={topTippers.topTippers}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis
                    dataKey="username"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    stroke="#9ca3af"
                    style={{ fontSize: "14px", fontWeight: "500" }}
                  />
                  <YAxis
                    stroke="#9ca3af"
                    style={{ fontSize: "14px" }}
                    label={{
                      value: "Times Tipped",
                      angle: -90,
                      position: "insideLeft",
                      style: { fill: "#9ca3af" },
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1f2937",
                      border: "1px solid #a78bfa",
                      borderRadius: "8px",
                      color: "#ffffff",
                    }}
                    labelStyle={{
                      color: "#ffffff",
                      fontWeight: "bold",
                    }}
                    itemStyle={{
                      color: "#e5e7eb",
                    }}
                    cursor={{ fill: "rgba(139, 92, 246, 0.1)" }}
                  />
                  <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                    {topTippers.topTippers.map((_: any, index: number) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          index === 0
                            ? "#fbbf24" // Gold
                            : index === 1
                            ? "#9ca3af" // Silver
                            : index === 2
                            ? "#f97316" // Bronze
                            : "#8b5cf6" // Purple
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <p className="text-gray-400 text-lg">
                No tips found for this amount in the selected time range.
              </p>
            </div>
          )}
        </div>

        {/* Results List */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden mt-6">
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 px-6 py-4">
            <h2 className="text-xl font-bold text-white">Detailed Rankings</h2>
          </div>

          {topTippers?.topTippers && topTippers.topTippers.length > 0 ? (
            <div className="divide-y divide-gray-700">
              {topTippers.topTippers.map(
                (
                  tipper: { username: string; count: number },
                  index: number
                ) => (
                  <div
                    key={tipper.username}
                    className="px-6 py-4 flex items-center justify-between hover:bg-gray-700 transition"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex items-center justify-center w-12 h-12 rounded-full font-bold text-white ${
                          index === 0
                            ? "bg-yellow-500"
                            : index === 1
                            ? "bg-gray-400"
                            : index === 2
                            ? "bg-orange-600"
                            : "bg-gray-600"
                        }`}
                      >
                        #{index + 1}
                      </div>
                      <div>
                        <p className="text-lg font-semibold text-white">
                          {tipper.username}
                        </p>
                        <p className="text-sm text-gray-400">
                          {tipper.count} {tipper.count === 1 ? "tip" : "tips"}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-purple-400">
                        {tipper.count}
                      </p>
                      <p className="text-xs text-gray-500">times tipped</p>
                    </div>
                  </div>
                )
              )}
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <p className="text-gray-400 text-lg">
                No tips found for this amount in the selected time range.
              </p>
            </div>
          )}
        </div>

        {/* Tip Info */}
        <div className="mt-8 bg-blue-900 border border-blue-700 rounded-lg p-4">
          <p className="text-blue-100 text-sm">
            <span className="font-semibold">💡 Tip:</span> This page shows the
            top 5 supporters who have tipped the selected amount within your
            chosen time range. Use this to recognize your most loyal supporters
            for specific actions!
          </p>
        </div>
      </div>
    </div>
  );
};

export default TopTippersPage;
